/**
 * Abrir os autos de um processo — na mesma aba ou em outra (docs/PLANO_BANCADA.md,
 * Bloco 2). A aba nova é FÁCIL de pedir e nunca imposta (decisão de 27/08,
 * reafirmada em 26/10):
 *
 * - Ctrl+clique (⌘ no Mac) e o botão do meio abrem os autos em outra aba, no modo
 *   janela (sem o menu), numa aba por processo — clicar de novo no mesmo processo
 *   reaproveita a aba em vez de acumular cópias (`lib/slim`). É o gesto que o
 *   advogado já usa em qualquer site; antes, nas linhas do Acervo e nos botões que
 *   navegavam por código, ele não fazia nada além do clique comum.
 * - A preferência "Abrir os autos sempre em nova aba" (Configurações, desligada por
 *   padrão) faz do clique comum o mesmo gesto. Ela não vale no celular nem no app
 *   instalado (PWA): lá a aba nova tira a pessoa do app.
 */
import { useCallback, useSyncExternalStore } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuthOpcional } from '@/context/AuthProvider';
import { useNotification } from '@/context/NotificationProvider';
import { chaveDePreferencia, gravarPreferencia, lerPreferencia } from '@/lib/preferenciasUsuario';
import { abrirAutosEmNovaAba } from '@/lib/slim';

import { useIsDesktop, useMediaQuery } from './useMediaQuery';

const PREFERENCIA = 'jurisapp.autos.nova_aba';

/**
 * O gesto do clique pede aba nova? Ctrl, ⌘, Shift (que o navegador faria virar uma
 * janela do app completo, com o menu) ou o botão do meio.
 */
export function gestoDeNovaAba(evento?: {
  ctrlKey?: boolean;
  metaKey?: boolean;
  shiftKey?: boolean;
  button?: number;
}): boolean {
  return !!evento && (!!evento.ctrlKey || !!evento.metaKey || !!evento.shiftKey || evento.button === 1);
}

// Quem lê a preferência é avisado quando ela muda: nesta aba (o cartão de
// Configurações grava) e nas outras (o evento `storage` do navegador). Lida só no
// desenho, a tela já montada seguia com o valor velho até redesenhar.
const MUDOU = 'jurisapp:preferencia-autos-nova-aba';
function assinar(aviso: () => void): () => void {
  window.addEventListener('storage', aviso);
  window.addEventListener(MUDOU, aviso);
  return () => {
    window.removeEventListener('storage', aviso);
    window.removeEventListener(MUDOU, aviso);
  };
}

/** A preferência "Abrir os autos sempre em nova aba", por usuário. */
export function usePreferenciaAutosEmNovaAba(): [boolean, (valor: boolean) => void] {
  // Opcional: as listas que abrem os autos (o Acervo) também montam fora do provider,
  // nos testes. Sem identidade não há chave — a preferência vale como desligada.
  const user = useAuthOpcional()?.user;
  const chave = chaveDePreferencia(PREFERENCIA, user?.id);
  const valor = useSyncExternalStore(assinar, () => lerPreferencia(chave) === '1');
  const definir = (novo: boolean) => {
    gravarPreferencia(chave, novo ? '1' : '0');
    window.dispatchEvent(new Event(MUDOU));
  };
  return [valor, definir];
}

/** Onde a preferência vale: no computador e fora do app instalado. */
export function usePreferenciaDeAbaAplicavel(): boolean {
  const isDesktop = useIsDesktop();
  const instalado = useMediaQuery('(display-mode: standalone)');
  return isDesktop && !instalado;
}

interface OpcoesDeAbertura {
  /** `?view=movimentos`, `?doc=…` — o mesmo na aba nova. */
  search?: string;
  /** O `state` da navegação na mesma aba (ex.: a volta ao Acervo). */
  state?: unknown;
  /** O clique que pediu: Ctrl, ⌘ ou o botão do meio pedem aba nova. */
  evento?: { ctrlKey?: boolean; metaKey?: boolean; shiftKey?: boolean; button?: number; preventDefault?: () => void };
}

/**
 * Decide se o clique vai para outra aba (`true`) — o gesto ou a preferência —, sem
 * navegar. Para quem precisa decidir antes (um link que deixa o navegador seguir).
 */
export function useVaiParaNovaAba(): (evento?: OpcoesDeAbertura['evento']) => boolean {
  const [sempre] = usePreferenciaAutosEmNovaAba();
  const aplicavel = usePreferenciaDeAbaAplicavel();
  return useCallback((evento) => gestoDeNovaAba(evento) || (sempre && aplicavel), [sempre, aplicavel]);
}

/** Abre os autos do processo na mesma aba ou em outra, conforme o gesto e a preferência. */
export function useAbrirAutos(): (numero: string, opcoes?: OpcoesDeAbertura) => void {
  const navigate = useNavigate();
  const { showError } = useNotification();
  const vaiParaNovaAba = useVaiParaNovaAba();
  return useCallback(
    (numero, { search = '', state, evento } = {}) => {
      if (vaiParaNovaAba(evento)) {
        evento?.preventDefault?.();
        if (!abrirAutosEmNovaAba(numero, search)) {
          showError('O navegador bloqueou a nova aba. Autorize os pop-ups deste site.');
        }
        return;
      }
      navigate(`/app/processos/${encodeURIComponent(numero)}/autos${search}`, { state });
    },
    [navigate, showError, vaiParaNovaAba],
  );
}

/**
 * O botão do meio sobre a LINHA (do Acervo, de uma lista) — e não sobre um controle
 * dela. Os controles (estrela, tags, ⋮, seleção, copiar) só param o `click`; o
 * `auxclick` subia até a linha, inclusive dos portais (menus, popovers), e abria os
 * autos. Portais não estão DENTRO da linha no DOM, e controles têm papel interativo.
 */
export function cliqueDoMeioNaLinha(evento: {
  button: number;
  target: EventTarget;
  currentTarget: EventTarget;
}): boolean {
  if (evento.button !== 1) return false;
  const alvo = evento.target as Element | null;
  const linha = evento.currentTarget as Element;
  if (!alvo || !linha.contains(alvo)) return false;
  const controle = alvo.closest('button, a, input, label, [role="checkbox"], [role="menuitem"], [role="button"]');
  return !controle || controle === linha || !linha.contains(controle);
}
